import { useSearchParams, useNavigate } from 'react-router-dom';
import { useClinicBasePath } from '@/hooks/useClinicBasePath';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineGrid,
  Text,
  SkeletonBodyText,
  EmptyState,
  Box,
  Thumbnail,
} from '@shopify/polaris';
import { clinicPortalApi } from '@/services';

interface RentalProduct {
  shopifyProductId: string;
  title: string;
  imageUrl: string | null;
  rentalConfig: {
    isRentalProduct: boolean;
    weeklyRateCents: number | null;
  };
}

export default function ClinicCreateOrderProducts() {
  const navigate = useNavigate();
  const base = useClinicBasePath();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId') || '';

  const { data: products, isLoading, error } = useQuery<RentalProduct[]>({
    queryKey: ['clinic-products'],
    queryFn: () => clinicPortalApi.getProducts(),
  });

  const rentalProducts = (products || []).filter((p) => p.rentalConfig.isRentalProduct);

  if (error) {
    return (
      <Page title="Create Order" backAction={{ content: 'Patients', onAction: () => navigate(`${base}/patients`) }}>
        <Card>
          <EmptyState
            heading="Error loading products"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title="Create Order"
      subtitle="Product List"
      backAction={{ content: 'Patients', onAction: () => navigate(`${base}/patients`) }}
    >
      {isLoading ? (
        <Card>
          <SkeletonBodyText lines={8} />
        </Card>
      ) : rentalProducts.length === 0 ? (
        <Card>
          <EmptyState
            heading="No rental products available"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>No Game Ready products are configured for rental.</p>
          </EmptyState>
        </Card>
      ) : (
        <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="400">
          {rentalProducts.map((product) => (
            <div
              key={product.shopifyProductId}
              onClick={() =>
                navigate(
                  `${base}/orders/new/configure?patientId=${patientId}&productId=${encodeURIComponent(product.shopifyProductId)}`,
                )
              }
              style={{ cursor: 'pointer' }}
            >
              <Card>
                <BlockStack gap="300" inlineAlign="center">
                  <Text as="h3" variant="headingMd" alignment="center">
                    {product.title}
                  </Text>
                  <Box paddingBlock="200">
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt={product.title}
                        style={{
                          width: '100%',
                          maxWidth: '200px',
                          height: 'auto',
                          display: 'block',
                          margin: '0 auto',
                        }}
                      />
                    ) : (
                      <Thumbnail
                        source=""
                        alt={product.title}
                        size="large"
                      />
                    )}
                  </Box>
                </BlockStack>
              </Card>
            </div>
          ))}
        </InlineGrid>
      )}
    </Page>
  );
}
